import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { RiAlarmWarningLine, RiAlertLine, RiErrorWarningLine, RiFile2Fill, RiNewspaperLine, RiSearchLine, RiUserLine, RiUserStarLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Row } from "reactstrap";
import ComplianceScreeningDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/screening/DashboardService";

const Widget = ({
    title,
    error,
    amount,
    t,
    icon
}) => {
    return (
        <Card className="h-100">
            <CardBody className="pb-1">
                <div className="d-flex">
                    <div className="overflow-hidden" style={{ flex: "0 0 80%" }}>
                        <p
                            className="font-size-14 mb-2"
                            style={{ textTransform: "capitalize" }}>
                            {t(title)}
                        </p>
                        <h4 className={`mb-0 ${error?.hasError ? 'text-danger' : ''}`}>
                            {error?.hasError ? (
                                `${t('Error')}`
                            ) : amount}
                        </h4>
                        {error?.hasError && (
                            <small className="text-danger">
                                {t(
                                    error?.errorStatusCode === 403 ?
                                        'Please upgrade your plan to access this feature' :
                                        'Unable to load data at this time. Please refresh the page or try again later.'
                                )}
                            </small>
                        )}
                    </div>

                    <div className="text-center">
                        {icon}
                    </div>

                    {/* {icon && (
                        <div
                            className="text-center"
                            dir="ltr"
                            style={{ height: 66 }}>
                            <i
                                className={icon + " font-size-24"}
                                style={{ position: "relative", top: 12, right: 6 }}
                            ></i>
                        </div>
                    )} */}
                </div>
            </CardBody>
        </Card>
    )
}

const Widgets = ({
    t,
    filters
}) => {

    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-screening-dashboard",
            "screening-results",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceScreeningDashboardService.getInstance();

            return await service.fetchScreeningResults(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        },
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Row>
                <Col sm="12">
                    <Row>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                        <Col lg="3" sm="6" className="mb-4">
                            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }}></div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    const error = {
        hasError: fetchDataQuery.isError,
        errorStatusCode: fetchDataQuery?.error?.response?.status
    };

    const screeningResults = fetchDataQuery.data || {};

    const iconStyle = {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }

    return (
        <Row>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalSearchItems || 0}
                    title='Total Search Items'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#D9E9FF',
                            color: '#2F80ED'
                        }}>
                            <RiFile2Fill size="24" />
                        </div>
                    }
                    error={error}
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalHits || 0}
                    title='Total Hits'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#FFD8D8',
                            color: '#822626'
                        }}>
                            <RiErrorWarningLine size="24" />
                        </div>}
                    error={error}
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalEntitiesUnderScreening || 0}
                    title='Total Screened Entities'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#D9E9FF',
                            color: '#2F80ED'
                        }}>
                            <RiSearchLine size="24" />
                        </div>
                    }
                    error={error}
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalIndividualsUnderScreening || 0}
                    title='Total Screened Individuals'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#CBD1FF',
                            color: '#4D61FF'
                        }}>
                            <RiUserLine size="24" />
                        </div>
                    }
                    error={error} 
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalSanctions || 0}
                    title='Total Sanctions'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#FFD8D8',
                            color: '#822626'
                        }}>
                            <RiAlertLine size="24" />
                        </div>
                    }
                    error={error} 
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalPeps || 0}
                    title='Total PEPs'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#CBD1FF',
                            color: '#4D61FF'
                        }}>
                            <RiUserStarLine size="24" />
                        </div>
                    }
                    error={error}
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalCriminal || 0}
                    title='Total Criminal Records'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#FFE4D9',
                            color: '#FF763F'
                        }}>
                            <RiAlarmWarningLine size="24" />
                        </div>
                    }
                    error={error} 
                    t={t}
                />
            </Col>
            <Col lg="3" sm="6" className="mb-4">
                <Widget
                    amount={screeningResults.totalAdverse || 0}
                    title='Total Adverse News'
                    icon={
                        <div style={{
                            ...iconStyle,
                            backgroundColor: '#D9E9FF',
                            color: '#2F80ED'
                        }}>
                            <RiNewspaperLine size="24" />
                        </div>
                    }
                    error={error} 
                    t={t}
                />
            </Col>
        </Row>
    );
}

export default withNamespaces()(
    Widgets
);